/**
 * O vocabulário da tela da Shopee (F1/F2). Só texto e escolha de canal (cor + ícone + palavra):
 * nenhum número é calculado aqui (invariante 3). O que chega do servidor já vem pronto —
 * comissão em centavos, lucro por lead em milésimos de centavo, a faixa, a lacuna.
 */
import { CircleCheck, KeyRound, TriangleAlert } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { ShopeeBlockGap, ShopeeConnectionView } from '../../lib/api';
import { diasAntes } from '../earnings/earnings-ui';

export interface StatusSpec {
  label: string;
  tone: 'success' | 'warning' | 'danger';
  icon: LucideIcon;
}

/** Estado de sistema em dois canais: cor + ícone + palavra. */
export const STATUS_DA_CONEXAO: Record<ShopeeConnectionView['status'], StatusSpec> = {
  ACTIVE: { label: 'Conectado', tone: 'success', icon: CircleCheck },
  AUTH_FAILED: { label: 'Senha recusada', tone: 'danger', icon: KeyRound },
  CREDENTIAL_MISSING: { label: 'Senha ilegível', tone: 'warning', icon: KeyRound },
  ERROR: { label: 'Última importação falhou', tone: 'warning', icon: TriangleAlert },
};

/**
 * Por que o bloco não tem lucro por lead — a frase diz o que falta, sem falar em API, id ou
 * sincronização (G6 da spec).
 */
export const LACUNA_DO_BLOCO: Record<ShopeeBlockGap, string> = {
  SEM_MEDICAO: 'A comissão destes dias ainda não chegou da Shopee.',
  SEM_SUB_ID: 'Ligue um Sub ID a este bloco para ver quanto ele rende.',
  SEM_GRUPOS: 'Este bloco não tem grupo ativo — não há gente para dividir.',
  SEM_CONTAGEM: 'Faltou a contagem de gente de algum grupo do bloco nestes dias.',
};

/** Quantos dias medidos a tela mostra de uma vez. */
export const DIAS_NA_TELA = 7;

/**
 * O período da tela: os últimos `DIAS_NA_TELA` dias da janela que o SERVIDOR mediu. Nunca o
 * relógio do navegador (invariante 10). `null` = nada medido ainda.
 */
export function periodoDaJanela(
  measured: ShopeeConnectionView['measured'],
): { since: string; until: string } | null {
  if (measured === null) return null;
  const inicio = diasAntes(measured.through, DIAS_NA_TELA - 1);
  return { since: inicio > measured.from ? inicio : measured.from, until: measured.through };
}

/** Instante → "07/10 às 14:05", no fuso do WORKSPACE. */
export function quandoNoFuso(iso: string, timezone: string): string {
  const d = new Date(iso);
  const dia = new Intl.DateTimeFormat('pt-BR', {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
  }).format(d);
  const hora = new Intl.DateTimeFormat('pt-BR', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
  return `${dia} às ${hora}`;
}

/** Validação do formulário — espelho do servidor (`shopee.router.ts`), para a recusa ensinar. */
export const APP_ID_REGEX = /^\d{5,20}$/;
export const APP_SECRET_REGEX = /^[\x21-\x7e]{8,256}$/;
